<template>
  <div class="price-card__container">
    <div class="price-card__price-section">
      <div class="price-card__price-row">
        <h5 class="price-card__label">Загальна вартість:</h5>
        <div class="price-card__price-group">
          <SpinnerLoader
            v-if="isPriceLoading"
            class="price-card__loader"
          />
          <div
            v-if="!isPriceLoading"
            class="price-card__price"
          >
              <div
                v-show="discountAmount"
                class="price-card__discount-group"
              >
                <span class="price-card__old-price">{{ formatNumber(oldPrice, { suffix: ' грн', precision: 2 }) }}</span>
                <span class="price-card__discount">-{{ formatNumber(discountAmount, { suffix: ' грн', precision: 2 }) }}</span>
              </div>
            <span class="price-card__current-price">{{ formatNumber(finishPrice, { precision: 2 }) }} <span class="price-card__currency">грн</span></span>
          </div>
        </div>
      </div>
      <transition name="expand">
        <div
          v-show="(openDetails || appliedBonus > 0) && !isPriceLoading"
          class="price-card__earnings-row"
        >
          <h5 class="price-card__label">Ваш заробіток:</h5>
          <div class="price-card__price-group">
            <span class="price-card__earnings">{{ formatNumber(totalEarnings, { precision: 2 }) }} <span class="price-card__currency">грн</span></span>
          </div>
        </div>
      </transition>
      <transition name="expand">
        <div
          v-if="appliedBonus > 0"
          class="price-card__earnings-row"
        >
          <h5 class="price-card__label">Комісійна винагорода:</h5>
          <div class="price-card__price-group">
            <span class="price-card__earnings">{{ formatNumber(earnings, { precision: 2 }) }} <span class="price-card__currency">грн</span></span>
          </div>
        </div>
      </transition>
      <transition name="expand">
        <div
          v-if="appliedBonus > 0"
          class="price-card__earnings-row bonus-earnings"
        >
          <h5 class="price-card__label">Бонусний заробіток:</h5>
          <div class="price-card__price-group">
            <span class="price-card__earnings">{{ formatNumber(appliedBonus, { precision: 2 }) }} <span class="price-card__currency">грн</span></span>
          </div>
        </div>
      </transition>
      <SmartLink
        class="price-card__details-link"
        :method="() => toggleShowDetails(!openDetails)"
      >
        <EyeHideIcon
          v-if="!openDetails"
          class="price-card__details-icon"
          color="#717680"
        />
        <EyeOpenIcon
          v-else
          class="price-card__details-icon"
          color="#717680"
        />
        Детальна інформація
      </SmartLink>
    </div>
    <div
      v-if="getUserLevelId"
      class="price-card__bonus-section"
    >
      <div class="price-card__bonus-content">
        <ToggleSwitch
          v-model="useBonuses"
          :disabled="!!bonusErrorMessage"
          class="price-card__bonus-toggle"
          @input="onBonusToggle"
        >
          Додати бонуси
          <HelpTooltip
            class="price-card__bonus-icon"
            text="За правилами бонусної системи додавання бонусів відбувається на максимальний розмір списання допустимий для вашого рівня. Якщо баланс бонусних балів меньший за максимальний розмір списання, то розмір використаних бонусів буде на сумму доступного балансу"
          />
        </ToggleSwitch>
        <p
          v-if="bonusErrorMessage"
          class="price-card__bonus-message"
        >
          {{ bonusErrorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { LocalStore } from '@/shared/lib/local-storage';
import { NumberFormatter } from '@/shared/lib/number';
import { HelpTooltip, ToggleSwitch } from '@/shared/ui/form';
import { EyeHideIcon, EyeOpenIcon } from '@/shared/ui/icons';
import { SmartLink } from '@/shared/ui/link';
import { SpinnerLoader } from '@/shared/ui/loader';

const { formatNumber } = NumberFormatter();
const detailsStore = new LocalStore('order_');

export default {
  props: {
    finishPrice: {
      type: Number,
      required: true,
    },
    oldPrice: {
      type: Number,
      default: 0,
    },
    discountAmount: {
      type: Number,
      default: 0,
    },
    earnings: {
      type: Number,
      default: 0,
    },
    isPriceLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openDetails: detailsStore.get('show-details', false),
      useBonuses: false,
      appliedBonus: 0,
    };
  },
  computed: {
    ...mapGetters('accountBonusesModule', ['getAvailableBonus']),
    ...mapGetters('accountBalanceModule', ['getBalanceAmount']),
    ...mapGetters('currentUserModule', ['getUserLevelId']),
    totalEarnings() {
      return parseFloat(this.earnings || 0) + parseFloat(this.appliedBonus || 0);
    },
    bonusErrorMessage() {
      if (this.getBalanceAmount !== null && this.getBalanceAmount < 0) {
        return 'Використовувати бонуси можна тільки за умови позитивного балансу в гаманці';
      }
      if (this.getAvailableBonus === 0) {
        return 'На вашому балансі відсутні бонуси для списання';
      }
      return null;
    },
  },
  created() {
    this.fetchLevels();
  },
  methods: {
    ...mapActions('accountBonusesModule', ['fetchLevels']),
    formatNumber,
    toggleShowDetails(open) {
      this.openDetails = open;
      detailsStore.set('show-details', open);
    },
    onBonusToggle(value) {
      if (value && this.bonusErrorMessage) {
        this.useBonuses = false;

        this.$emit('bonusApplied', null);
        return;
      }

      this.appliedBonus = value ? this.getAvailableBonus : 0;
      this.$emit('bonusApplied', this.appliedBonus);
    },
  },
  components: { SmartLink, EyeHideIcon, EyeOpenIcon, ToggleSwitch, HelpTooltip, SpinnerLoader },
};
</script>

<style scoped lang="scss">
.price-card {
  &__container {
    display: flex;
    gap: 16px;
    border: 1px solid #CBD5E1;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0px 1px 2px 0px #0000000F, 0px 1px 3px 0px #0000001A;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__price-section {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__price-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  &__loader {
    margin-right: 8px;
  }

  &__earnings-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  &__earnings-detail {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    padding-left: 8px;
  }

  &__detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__detail-label {
    font-size: 14px;
    font-weight: 400;
    color: #999DA6;
  }

  &__detail-value {
    font-size: 20px;
    font-weight: 700;
    color: #24282C;

    &--bonus {
      color: #50A64D;
    }
  }

  &__label {
    font-size: 20px;
    font-weight: 500;
    width: 220px;

    @media (max-width: 768px) {
      width: 100%;
      font-size: 16px;
    }
  }

  &__price-group {
    display: flex;
    flex-direction: column;
  }

  &__discount-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__old-price {
    font-size: 14px;
    font-weight: 400;
    text-decoration: line-through;
  }

  &__discount {
    background-color: #F50000;
    border-radius: 25px;
    font-size: 10px;
    color: #ffffff;
    padding: 2px 6px;
  }

  &__current-price {
    display: flex;
    align-items: center;
    font-size: 30px;
    font-weight: 600;
    color: #09090b;
  }

  &__earnings {
    display: flex;
    align-items: center;
    font-size: 30px;
    font-weight: 600;

    &.bonus-earnings {
      color: #50A64D;
    }
  }

  &__currency {
    font-size: 16px;
    font-weight: 400;
    text-transform: uppercase;
    margin-left: 8px;
  }

  &__details-link {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #717680;
    margin-top: 16px;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  &__details-icon {
    width: 16px;
    height: 16px;
    margin-right: 4px;
    flex-shrink: 0;
  }

  &__bonus-section {
    display: flex;
    align-items: center;
  }

  &__bonus-content {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
  }

  &__bonus-toggle {
    margin-bottom: 16px;
  }

  &__bonus-icon {
    flex-shrink: 0;
    margin-left: 8px;
    margin-top: 2px;
  }

  &__bonus-message {
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    font-weight: 400;
    color: #FF3B30;
    margin: 0;
    line-height: 1.4;
  }
}

::v-deep .toggle-label {
  width: 51px;
  height: 31px;

  &::before {
    width: 29px;
    height: 29px;
    top: 1px;
    left: 1px;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  max-height: 200px;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
